import React, { useCallback, useContext,  useState } from 'react'
import { Stack } from '@mui/material';
import { COLOR_MAP } from '../../../../constants/ColorNames';
import { PauseIcon } from '../../../../constants/icons/PauseIcon';
import { ResumeIcon } from '../../../../constants/icons/ResumeIcon';
import styled from '@emotion/styled';
import { useGetPauseCampaignMutation, useGetResumeCampaignMutation } from '../../../../redux/services/folderName';
import { ToastContext } from '../../../../utils/context/toastContext';
import BasicModal from '../../../../common/components/Modal';
import { Button } from '@foundit/ui-library';

type PauseCampaignPropsType = {
  campaignId: string;
  campaignName: string;
  status: string;
  rawStatus: string;
}

export const PauseCampaign = (props: PauseCampaignPropsType) => {
  const [triggerPause, { data, isLoading }] = useGetPauseCampaignMutation();
  const [triggerResume, { data: resumeData, isLoading: isResumeLoading }] = useGetResumeCampaignMutation();
  const { campaignId,  campaignName, rawStatus } = props;
  const { setToastValue } = useContext(ToastContext);
  const [openPauseModal, setOpenPauseModal] = useState<boolean>(false);
  const activeState = (rawStatus?.toLowerCase() === 'live' || rawStatus?.toLowerCase() === 'scheduled') ? 'Pause' : 'Resume';

  const showPause = ['live', 'paused', 'scheduled', 'auto_paused'].includes(rawStatus?.toLowerCase());


  const selectedColor =  COLOR_MAP.DARK_PURPLE


  const handlePauseCampaign = useCallback(() => {
    triggerPause(campaignId)
      .unwrap()
      .then((res) => {
        if (res) {
          const message = `Campaign "${campaignName}" paused successfully`
          setToastValue({
            isToastShown: true,
            toastMessage: message,
            toastKey: 'campaign-paused-success',
            toastSeverity: 'success'
          })
        }
      })
      .catch((err) => {
        const message = err.data?.message ? err.data.message  : 'Error while pausing the campaign';
        setToastValue({
          isToastShown: true,
          toastMessage: message,
          toastKey: 'campaign-paused-error',
          toastSeverity: 'error'
        })
      })

  }, [campaignId, setToastValue, triggerPause])

  const handleResumeCampaign = React.useCallback(() => {
    triggerResume(campaignId)
      .unwrap()
      .then((res) => {
        if (res) {
          const message = 'Campaign Resumed'
          setToastValue({
            isToastShown: true,
            toastMessage: message,
            toastKey: 'campaign-resume-success',
            toastSeverity: 'success'
          })
        }
      })
      .catch((err) => {
        const message = err.data?.message ? err.data.message  : 'Error while resuming the campaign';
        setToastValue({
          isToastShown: true,
          toastMessage: message,
          toastKey: 'campaign-resume-error',
          toastSeverity: 'error'
        })
      })

  }, [campaignId, setToastValue, triggerResume])

  const handleAction = useCallback(() => {
    if(activeState === 'Pause'){
      setOpenPauseModal(true);
    }
    else {
      handleResumeCampaign();
    }
  },[activeState, handlePauseCampaign, handleResumeCampaign])

  if (data || resumeData || !showPause)
    return null;

  return (
    <>
      <Button
        disabled={!showPause}
        type="text"
        size="small"
        loading={isLoading || isResumeLoading}
        onClick={handleAction}
        startIcon={
          activeState === "Pause" ? (
            <PauseIcon stroke={selectedColor} />
          ) : (
            <ResumeIcon fill={selectedColor} />
          )
        }
      >
        {activeState}
      </Button>

      <BasicModal
        handleClose={() => setOpenPauseModal(false)}
        action="Pause Campaign"
        loading={isLoading}
        handleAction={() => handlePauseCampaign()}
        open={openPauseModal}
        heading={"Pausing a campaign?"}
        dimensions={{ height: 211, width: 360 }}
        content={
          <div style={{ fontWeight: "500px", paddingTop: "1.6rem" }}>
            This will temporarily pause the campaign execution. You can resume
            it later from the Actions menu.
          </div>
        }
      />
    </>
  );
}
